import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, XIcon } from 'lucide-react';
function WistiaPlayer({
  isFloating,
  onDismiss
}: {
  isFloating: boolean;
  onDismiss: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Load Wistia player script
    const playerScript = document.createElement('script');
    playerScript.src = 'https://fast.wistia.com/player.js';
    playerScript.async = true;
    document.head.appendChild(playerScript);
    // Load Wistia embed script
    const embedScript = document.createElement('script');
    embedScript.src = 'https://fast.wistia.com/embed/183f5uriax.js';
    embedScript.async = true;
    embedScript.type = 'module';
    document.head.appendChild(embedScript);
    // Create the wistia-player element
    if (containerRef.current) {
      const player = document.createElement('wistia-player');
      player.setAttribute('media-id', '183f5uriax');
      player.setAttribute('aspect', '1.7777777777777777');
      player.style.width = '100%';
      player.style.display = 'block';
      containerRef.current.appendChild(player);
    }
    return () => {
      if (document.head.contains(playerScript)) document.head.removeChild(playerScript);
      if (document.head.contains(embedScript)) document.head.removeChild(embedScript);
    };
  }, []);
  return <div ref={containerRef} className={`relative z-10 w-full rounded-lg shadow-2xl overflow-hidden transition-all duration-500 ease-in-out ${isFloating ? 'fixed bottom-4 right-4 !w-[300px] md:!w-[380px] z-[90] rounded-xl border border-gold/30' : ''}`} style={isFloating ? {
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)'
  } : {}} data-id="element-159">
      <AnimatePresence data-id="element-160">
        {isFloating && <motion.button initial={{
        opacity: 0,
        scale: 0.5
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.5
      }} transition={{
        duration: 0.2
      }} onClick={onDismiss} className="absolute top-2 right-2 z-20 p-1.5 rounded-full bg-black/70 hover:bg-black/90 border border-white/20 transition-colors" aria-label="Cerrar video flotante" data-id="element-161">
            <XIcon className="h-3.5 w-3.5 text-white" data-id="element-162" />
          </motion.button>}
      </AnimatePresence>
    </div>;
}
export function Hero() {
  const [currentPrice, setCurrentPrice] = useState(27);
  const [isFloating, setIsFloating] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const videoSentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = videoSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(([entry]) => {
      // When the video's original position is not visible, float it
      if (!entry.isIntersecting) {
        setIsFloating(true);
      } else {
        setIsFloating(false);
        setIsDismissed(false); // Reset dismiss when scrolling back
      }
    }, {
      threshold: 0.1
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);
  const handleDismiss = () => {
    setIsDismissed(true);
  };
  return <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden px-4 py-20" data-id="element-163">
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy-light to-navy-dark" data-id="element-164">
        <motion.div animate={{
        scale: [1, 1.2, 1],
        opacity: [0.3, 0.5, 0.3]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-0 right-0 w-96 h-96 bg-gold/20 rounded-full blur-3xl" data-id="element-165" />
        <motion.div animate={{
        scale: [1.2, 1, 1.2],
        opacity: [0.2, 0.4, 0.2]
      }} transition={{
        duration: 10,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute bottom-0 left-0 w-96 h-96 bg-gold/10 rounded-full blur-3xl" data-id="element-166" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto" data-id="element-167">
        {/* Headline */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="text-center mb-6 md:mb-8" data-id="element-168">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="font-heading font-black text-4xl md:text-5xl lg:text-6xl leading-tight mb-4 md:mb-6" data-id="element-169">
            Gana Tu Primer <span className="text-gold" data-id="element-170">$1,000</span> Esta Semana
            Vendiendo Sitios Web a Negocios Locales
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="font-body text-lg md:text-xl text-gray-300 mb-4" data-id="element-171">
            (La IA los Construye en Minutos. Tú Solo Haces la Llamada.)
          </motion.p>
        </motion.div>

        {/* Hero Image */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="relative mb-6 md:mb-10" data-id="element-172">
          {/* Sentinel div to track original video position */}
          <div ref={videoSentinelRef} className="absolute inset-0 pointer-events-none" data-id="element-173" />
          <WistiaPlayer isFloating={!isDismissed && isFloating} onDismiss={handleDismiss} data-id="element-174" />

          <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-transparent rounded-lg pointer-events-none" data-id="element-175" />
        </motion.div>

        {/* Copy and CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 1
      }} className="text-center" data-id="element-176">
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 1.2
        }} className="font-body text-base md:text-lg text-gray-400 mb-6 md:mb-8 max-w-3xl mx-auto" data-id="element-177">
            Uno de nuestros miembros contactó 7 negocios en su primera semana.
            Cerró 3. Ganó $1,050. No sabía nada de diseño web. Tú tampoco
            necesitas saberlo.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 1.4
        }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-178">
            <motion.a href="/checkout-2" onClick={() => {
            if (window.fbq) window.fbq('track', 'AddToCart', {
              content_name: 'AI Web Closer Course',
              content_type: 'product',
              value: 27,
              currency: 'USD'
            });
          }} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="group bg-gold hover:bg-gold-light text-navy font-heading font-bold text-lg px-8 py-4 rounded-lg shadow-xl transition-all duration-300 flex items-center justify-center gap-2" data-id="element-179">
              Quiero Acceso por ${currentPrice}
              <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" data-id="element-180" />
            </motion.a>
          </motion.div>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 1.6
        }} className="font-body text-sm text-gray-500 mt-6" data-id="element-181">
            (VIDEO DE VENTAS AQUÍ)
          </motion.p>
        </motion.div>
      </div>
    </section>;
}